/**
 * Theme presets combining style, font, and color configurations.
 * Re-exports types from individual theme modules.
 */

import type { Style } from './theme-styles'
import type { ThemeColor } from './theme-colors'
import type { Font } from './theme-fonts'

// Re-export types for convenience
export type { Style } from './theme-styles'
export type { ThemeColor } from './theme-colors'
export type { Font } from './theme-fonts'

export type ColorMode = 'light' | 'dark' | 'system'
export type BaseColor = 'slate' | 'gray' | 'zinc' | 'neutral' | 'stone'
export type Radius = 'none' | 'sm' | 'default' | 'lg'

export interface ThemeConfig {
  mode: ColorMode
  baseColor: BaseColor
  themeColor: ThemeColor
  radius: Radius
  style: Style
  font: Font
  preset: string | null // null = custom
}

export interface ThemePreset {
  id: string
  name: string
  description: string
  config: Omit<ThemeConfig, 'preset'>
}

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'default',
    name: 'Vega / Inter',
    description: 'The classic MerchMuse theme',
    config: {
      mode: 'light',
      baseColor: 'slate',
      themeColor: 'blue',
      radius: 'default',
      style: 'vega',
      font: 'inter',
    },
  },
  {
    id: 'compact',
    name: 'Nova / Inter',
    description: 'Compact and efficient',
    config: {
      mode: 'light',
      baseColor: 'zinc',
      themeColor: 'indigo',
      radius: 'sm',
      style: 'nova',
      font: 'inter',
    },
  },
  {
    id: 'soft',
    name: 'Maia / Figtree',
    description: 'Soft and friendly',
    config: {
      mode: 'light',
      baseColor: 'stone',
      themeColor: 'emerald',
      radius: 'lg',
      style: 'maia',
      font: 'figtree',
    },
  },
  {
    id: 'sharp',
    name: 'Lyra / Noto Sans',
    description: 'Bold and modern',
    config: {
      mode: 'light',
      baseColor: 'neutral',
      themeColor: 'orange',
      radius: 'none',
      style: 'lyra',
      font: 'noto-sans',
    },
  },
  {
    id: 'midnight',
    name: 'Vega / Inter (Dark)',
    description: 'Dark mode professional',
    config: {
      mode: 'dark',
      baseColor: 'zinc',
      themeColor: 'violet',
      radius: 'default',
      style: 'vega',
      font: 'inter',
    },
  },
]

// Radius values in rem
export const RADIUS_VALUES: Record<Radius, string> = {
  none: '0',
  sm: '0.375rem',
  default: '0.5rem',
  lg: '0.75rem',
}

// Base color palettes - HSL values for light and dark modes
export const BASE_COLOR_PALETTES: Record<
  BaseColor,
  {
    light: {
      background: string
      foreground: string
      card: string
      cardForeground: string
      muted: string
      mutedForeground: string
      border: string
      input: string
    }
    dark: {
      background: string
      foreground: string
      card: string
      cardForeground: string
      muted: string
      mutedForeground: string
      border: string
      input: string
    }
  }
> = {
  slate: {
    light: {
      background: '210 40% 98%',
      foreground: '222 47% 11%',
      card: '0 0% 100%',
      cardForeground: '222 47% 11%',
      muted: '210 40% 96%',
      mutedForeground: '215 16% 47%',
      border: '214 32% 91%',
      input: '214 32% 91%',
    },
    dark: {
      background: '222 47% 8%',
      foreground: '210 40% 98%',
      card: '222 47% 11%',
      cardForeground: '210 40% 98%',
      muted: '217 33% 17%',
      mutedForeground: '215 20% 65%',
      border: '217 33% 17%',
      input: '217 33% 17%',
    },
  },
  gray: {
    light: {
      background: '0 0% 98%',
      foreground: '240 10% 4%',
      card: '0 0% 100%',
      cardForeground: '240 10% 4%',
      muted: '240 5% 96%',
      mutedForeground: '240 4% 46%',
      border: '240 6% 90%',
      input: '240 6% 90%',
    },
    dark: {
      background: '240 10% 4%',
      foreground: '0 0% 98%',
      card: '240 10% 8%',
      cardForeground: '0 0% 98%',
      muted: '240 4% 16%',
      mutedForeground: '240 5% 65%',
      border: '240 4% 16%',
      input: '240 4% 16%',
    },
  },
  zinc: {
    light: {
      background: '0 0% 100%',
      foreground: '240 10% 4%',
      card: '0 0% 100%',
      cardForeground: '240 10% 4%',
      muted: '240 5% 96%',
      mutedForeground: '240 4% 46%',
      border: '240 6% 90%',
      input: '240 6% 90%',
    },
    dark: {
      background: '240 10% 4%',
      foreground: '0 0% 98%',
      card: '240 10% 8%',
      cardForeground: '0 0% 98%',
      muted: '240 4% 16%',
      mutedForeground: '240 5% 65%',
      border: '240 4% 16%',
      input: '240 4% 16%',
    },
  },
  neutral: {
    light: {
      background: '0 0% 100%',
      foreground: '0 0% 4%',
      card: '0 0% 100%',
      cardForeground: '0 0% 4%',
      muted: '0 0% 96%',
      mutedForeground: '0 0% 45%',
      border: '0 0% 90%',
      input: '0 0% 90%',
    },
    dark: {
      background: '0 0% 4%',
      foreground: '0 0% 98%',
      card: '0 0% 8%',
      cardForeground: '0 0% 98%',
      muted: '0 0% 15%',
      mutedForeground: '0 0% 64%',
      border: '0 0% 15%',
      input: '0 0% 15%',
    },
  },
  stone: {
    light: {
      background: '60 9% 98%',
      foreground: '24 10% 10%',
      card: '60 9% 98%',
      cardForeground: '24 10% 10%',
      muted: '60 5% 96%',
      mutedForeground: '25 5% 45%',
      border: '20 6% 90%',
      input: '20 6% 90%',
    },
    dark: {
      background: '24 10% 10%',
      foreground: '60 9% 98%',
      card: '24 10% 12%',
      cardForeground: '60 9% 98%',
      muted: '12 6% 15%',
      mutedForeground: '24 5% 64%',
      border: '12 6% 15%',
      input: '12 6% 15%',
    },
  },
}

// Storage key for localStorage
export const THEME_STORAGE_KEY = 'merchmuse-theme'
